import fetchBuilder from "@tmi/utils/retry";

import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

import {
    Accepted,
    ApiError,
    BadRequest,
    Forbidden,
    InternalError,
    MultipleChoices,
    NoContent,
    NotFound,
    Ok,
    Unauthorized,
    UnprocessableContent
} from "@tmi/types/api/responses";
import { RuntimeException } from '@tmi/types/errors';
import type {
    ApiFetchResponse,
    BadRequestError,
    ErrorMessage,
    ErrorMessages,
    FetchOptions,
    UnprocessableContentError,
    TokenMessage
} from "@tmi/types/api/fetch";
import { clientStateManager } from "@/services/error-services";

export const defaultReasonForAbortingApiCall: string = "The API call has timed out.";
const userNotAuthorized: string = 'AUTH_NOT_AUTHORIZED'.toLowerCase();
const noValidCredentialsMessage: string = 'The current user is not authorized to access this resource'.toLowerCase();
const emptyResponseMessage: string = 'Unexpected end of JSON input'.toLowerCase();

function parseForUnauthorizedErrorBecauseWeDontDoAnythingConsistently( error: ErrorMessage ): boolean {
    return error.extensions.code?.toLowerCase().includes( userNotAuthorized ) ||
        error.extensions.HttpsStatusCode === Unauthorized.statusCode;
}

function parseForEmptyResponse( msg: string | undefined | null ): boolean {
    return msg?.toLowerCase().includes( emptyResponseMessage ) ?? false;
}

export function isOkStatus( status: number ): boolean {
    // Accept ONLY status code 200
    return status === Ok.statusCode;
}

export function isSuccessState( status?: number | undefined | null ): boolean {
    // Accepts anything within the 200 - 299 range
    return !!status && ( status >= Ok.statusCode && status < MultipleChoices.statusCode );
}

export function responseHasNoContent( status: number ): boolean {
    // Accept ONLY status code 204
    return NoContent.statusCode === status;
}

export function contentNotFound( status: number ): boolean {
    // Accept ONLY status code 404
    return NotFound.statusCode === status;
}

export const defaultTimeoutInMilliseconds = 7000; // 7s - allows complex queries while staying under server's 8s timeout
export function normalizeFetchParameters< OT >( { serverBaseUri, verbOverride, timeout, endpoint, payload, forceThrowExceptions }: FetchOptions< OT > ): FetchParameters {
    const baseUri: string = serverBaseUri ?? `${ import.meta.env.VITE_FLAGSHIP_BASE_URI }`;
    return {
        baseUri: baseUri,
        method: verbOverride ?? 'POST',
        uri: `${baseUri}${endpoint}`,
        body: payload ? JSON.stringify( payload ) : null,
        maxApiDuration: timeout ?? defaultTimeoutInMilliseconds,
        forceThrowExceptions: forceThrowExceptions ?? false
    };
}

export function buildStandardHeaders( authToken?: string | null | undefined ): HeadersInit {
    const headers: HeadersInit = {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    }

    if ( authToken ) {
        headers.Authorization = `Bearer ${ authToken }`;
    }

    return headers;
}

type FetchParameters = {
    baseUri: string,
    method: string,
    uri: string,
    body: string | null,
    maxApiDuration: number,
    forceThrowExceptions: boolean
}

// Import checkForValidSession for verifying if auth errors are truly session expiration
import { checkForValidSession } from "@/stores/Auth";

// Session verification cache to prevent spamming the session check API
let lastSessionCheck: {
    timestamp: number;
    isValid: boolean;
    token: string;
} | null = null;

const SESSION_CHECK_CACHE_MS = 30000; // 30 seconds - balances API spam prevention vs catching session expiration

export function clearSessionCache(): void {
    lastSessionCheck = null;
}

/**
 * Check if session is valid with caching to prevent API spam
 * When multiple API calls fail with auth errors simultaneously, we only check the session once
 * and cache the result for 30 seconds
 */
async function checkSessionWithCache( authToken: string | null ): Promise< boolean > {
    // No token = not valid
    if ( !authToken ) return false;

    // Check if we have a recent cached result for this token
    const now = Date.now();
    if ( lastSessionCheck &&
        lastSessionCheck.token === authToken &&
        now - lastSessionCheck.timestamp < SESSION_CHECK_CACHE_MS ) {
        logger.debug( 'Using cached session validity check' );
        return lastSessionCheck.isValid;
    }

    // Cache miss or expired - actually check the session
    logger.debug( 'Checking session validity (cache miss or expired)' );
    const isValid = await checkForValidSession( authToken );

    // Cache the result
    lastSessionCheck = {
        timestamp: now,
        isValid,
        token: authToken
    };

    return isValid;
}

// Check if GraphQL error has an error code (indicates a handled/critical error vs benign data error)
// Benign errors (no data found, validation) don't have extensions.code
// Critical errors (infrastructure, configuration) have extensions.code
function isCriticalGraphQLError( error: ErrorMessage ): boolean {
    return !!error.extensions?.code;
}

/**
 * Centralized auth error handling with session verification
 * Returns true if session is expired (should reject), false if session is valid (permission issue)
 */
async function handleAuthError( authToken: string | null, reject: ( reason: any ) => void, errorContext: string ): Promise< boolean > {
    const isSessionValid = await checkSessionWithCache( authToken );
    if ( !isSessionValid ) {
        // Session is truly expired - reject so calling code can logout/redirect
        reject( new Unauthorized() );
        return true;
    }
    // Session is valid - this is a permission issue for this specific resource, not auth failure
    logger.warn( `${errorContext} but session is still valid - likely permission issue for this resource` );
    return false;
}

async function handleErrorConditions( errors: ErrorMessage[] | undefined, authToken: string | null, reject: ( reason: any ) => void ): Promise< boolean > {
    if ( errors ) {
        for ( const e of errors ) {
            if ( parseForUnauthorizedErrorBecauseWeDontDoAnythingConsistently( e ) ) {
                return await handleAuthError( authToken, reject, 'Received AUTH_NOT_AUTHENTICATED' );
            }

            if ( e.message.toLowerCase().includes( noValidCredentialsMessage ) ) {
                return await handleAuthError( authToken, reject, 'Received Forbidden' );
            }

            // Check for critical errors with error codes (infrastructure, configuration issues)
            // Errors without codes are benign (data not found, validation) and should be logged but not fail
            if ( isCriticalGraphQLError( e ) ) {
                const errorCode = e.extensions.code;

                // Special handling for AUTH_NOT_AUTHENTICATED error code
                if ( errorCode === 'AUTH_NOT_AUTHENTICATED' ) {
                    return await handleAuthError( authToken, reject, 'Received AUTH_NOT_AUTHENTICATED code' );
                }

                reject( new ApiError( 503, `GraphQL error [${errorCode}]: ${e.message}` ) );
                return true;
            }
        }
    }
    return false; // No critical errors found
}

/**
 * TODO: FUTURE REFACTORING NEEDED
 *
 * Current Issues:
 * 1. Error classification scattered (GraphQL errors, HTTP status codes, retry whitelist)
 * 2. Auth/Authz semantics mixed (401/403 sometimes means session expired, sometimes permission denied)
 * 3. Inconsistent return pattern (sometimes throws, sometimes returns null)
 * 4. Session verification duplicated in multiple places
 *
 * Proposed Future Architecture:
 * - Single classifyError() function to determine error severity, retry-ability, and behavior
 * - Separate SessionExpired from PermissionDenied errors
 * - Consistent pattern: throw only SessionExpired, return null for all other errors
 * - Centralized error configuration for easy maintenance
 *
 * For now: Current implementation solves immediate problems (infinite retries, API spam)
 * but needs architectural cleanup when time permits thorough testing.
 */
export async function goFetch< T, OT >(
        { serverBaseUri, endpoint, verbOverride, payload, authToken, timeout, forceThrowExceptions }: FetchOptions< OT > ):
            Promise< ApiFetchResponse< T > | null | any > {

    // Removed client faulting mechanism - stores and sync coordinators now handle failures gracefully
    // with their own cooldowns and consecutive failure counters

    // Normalize authToken to string | null (remove undefined)
    const normalizedAuthToken = authToken ?? null;

    const fetchParams = normalizeFetchParameters( {
        serverBaseUri,
        verbOverride,
        timeout,
        endpoint,
        payload,
        forceThrowExceptions
    } );

    const headers = buildStandardHeaders( normalizedAuthToken );

    // Create a new Promise to encapsulate the asynchronous API call
    return new Promise( ( resolve, reject ) => {
        // Create an AbortController to short-circuit an API call that is taking too long
        const monitor = new AbortController();
        const timeoutId = setTimeout( () => monitor.abort( defaultReasonForAbortingApiCall ), fetchParams.maxApiDuration );

        // WHITELIST: Only retry these specific status codes
        const defaultRetryOptions = {
            retries: 1, // Reduced from 2 to 1 - allows one retry (2 total attempts) to limit cascading failures
            retryDelay: 1000,
            maxRetryDelay: 2000,
            retryOn: [ 408, 504 ] // ONLY retry timeouts (removed 503 - can indicate resource exhaustion, not just temporary unavailability)
        };

        const fetcher = fetchBuilder( fetch, defaultRetryOptions );

        // Perform the API call using fetch or any other suitable method
        fetcher( fetchParams.uri,  {
            method: fetchParams.method,
            body: fetchParams.body,
            headers: headers,
            signal: monitor.signal
        } ).then( async ( response: Response ): Promise< void > => {
            // Clear the timout at the earliest point to avoid false negative results
            clearTimeout( timeoutId );

            logger.debug( 'response received: ', response );
            if ( response.ok ) {
                let result: any = {};
                try {
                    result = await response.json();
                } catch ( err: any ) { /* IGNORE */ }

                const hasErrorsProperty = result.hasOwnProperty( 'errors' );
                if ( hasErrorsProperty ) {
                    // Check if errors contain critical issues (auth, resources, etc.)
                    const hasCriticalError = await handleErrorConditions( result.errors, normalizedAuthToken, reject );

                    // If critical error was found, handleErrorConditions already rejected - just return
                    if ( hasCriticalError ) {
                        return;
                    }

                    // Non-critical GraphQL errors (data not found, validation, etc.) - log but continue
                    // This allows pagination to skip bad data and continue with good data
                    logger.warn( 'GraphQL query returned non-critical errors:', result.errors );
                }

                const hasDataProperty = result.hasOwnProperty('data' );
                // Resolve the Promise with the response data
                resolve( {
                    data: hasDataProperty ? result.data : result,
                    status: response.status
                } );
            } else {
                // First, parse the response text to determine if the error is something that can be handled
                const text: string = await response.text();
                let responseText: ErrorMessages | undefined;

                try
                {
                    responseText = JSON.parse( text );
                    await handleErrorConditions( responseText?.errors, normalizedAuthToken, reject );
                } catch ( err: any ) { /* IGNORE */ }

                // Reject the Promise with the original reason
                const statusCode = response.status;
                logger.debug( `API call failed with status: ${ statusCode }` );

                // TODO: Support any other errors that are seen regularly
                // TODO: Add in automatic client logging & metrics collection
                switch ( response.status ) {
                    case Unauthorized.statusCode:
                        // Check session validity - only reject with Unauthorized if truly expired
                        const isSessionValid401 = await checkSessionWithCache( normalizedAuthToken );
                        if ( !isSessionValid401 ) {
                            reject( new Unauthorized() );
                        } else {
                            // Session valid - permission issue for this specific resource
                            reject( new ApiError( 401, 'Unauthorized for this resource but session valid' ) );
                        }
                        break;
                    case Forbidden.statusCode:
                        // Check session validity - treat expired session as Unauthorized
                        const isSessionValid403 = await checkSessionWithCache( normalizedAuthToken );
                        reject( !isSessionValid403 ? new Unauthorized() : new Forbidden() );
                        break;
                    case NotFound.statusCode:
                        reject( new NotFound() );
                        break;
                    case UnprocessableContent.statusCode:
                        try
                        {
                            const error: UnprocessableContentError = JSON.parse( text );
                            const tokenMessage: TokenMessage = JSON.parse( error.detail );
                            reject( new RuntimeException( tokenMessage.token[ 0 ] ) );
                        } catch { reject( new RuntimeException( '' ) );}
                        break;
                    case BadRequest.statusCode:
                        try{
                            const errorMessage: BadRequestError = JSON.parse( text );
                            reject( new RuntimeException( errorMessage.Message ) );
                        } catch { reject( new RuntimeException( '' ) );}
                        break;
                    case InternalError.statusCode:
                        console.error( 'THIS IS THE CAPTURING CONTEXT: ', response );
                        reject( new InternalError() );
                        break;
                    default:
                        reject( new ApiError( response.status, response.statusText ) );
                        break;
                }
            }
        } )
        // If the timeout was triggered, any access of the response after the face will throw an AbortError exception
        .catch( ( err: any ) => {
            // As a generally good practice, always clean up any resources that may or may not be active
            clearTimeout( timeoutId );

            // Automatically log the error message (only for actual errors, not empty responses)
            if ( typeof err === 'string' ) {
                // String errors are rare, don't log them
            } else {
                const message = JSON.stringify( err.message );
                if ( parseForEmptyResponse( message ) ) {
                    // Empty response - resolve as success with empty data
                    resolve( {
                        data: {} as T,
                        status: Accepted.statusCode
                    } );
                    return;
                }
                // Don't log warnings for every failed API call - too noisy
            }

            // TODO: Add in automatic metrics and client error monitoring

            // Reject the Promise with any error that occurs during the API call
            reject( err );
        } );
    } ).catch( ( err: any ) => {
        // Handle Unauthorized errors immediately - trigger logout before throwing
        // This ensures logout happens even if calling stores don't have proper error handling
        if ( err instanceof Unauthorized ) {
            clearSessionCache();
            clientStateManager.queueClientError( err, {
                endpoint: fetchParams.uri,
                payload: payload
            } );
        }

        if(fetchParams.forceThrowExceptions) {
            throw err;
        }
        else if ( typeof err === 'object' && !( err instanceof RuntimeException )) {
            // Critical errors (auth/permissions) should be thrown so calling code can handle them
            // (e.g., redirect to login, show forbidden page)
            if ( err instanceof Unauthorized || err instanceof Forbidden ) {
                throw err;
            }

            // Non-critical errors return null to allow graceful degradation
            // Stores/coordinators handle null responses with cooldowns and consecutive failure counters
            return null;
        }
        else {
            throw err;
        }
    } );
}
